/**
 * Event Tracking Service
 * Tracks user behavior and sends events to the backend for analytics
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

class EventTracker {
  constructor() {
    this.queue = [];
    this.isProcessing = false;
    this.batchSize = 10;
    this.flushInterval = 5000; // 5 seconds
    
    // Start batch processing
    this.startBatchProcessing();
  }

  /**
   * Get auth token from localStorage
   */
  getToken() {
    return localStorage.getItem('access_token');
  }

  /**
   * Track an event
   * @param {string} eventType - Type of event (e.g., 'page.view', 'search', 'click')
   * @param {object} metadata - Additional event data
   */
  track(eventType, metadata = {}) {
    const event = {
      event_type: eventType,
      metadata: {
        ...metadata,
        url: window.location.pathname,
        referrer: document.referrer,
        timestamp: new Date().toISOString()
      },
      source: 'web'
    };

    this.queue.push(event);

    // Immediate flush for important events
    if (['page.view', 'search', 'export'].some(type => eventType.startsWith(type))) {
      this.flush();
    }
  }

  /**
   * Track page view
   * @param {string} pageName - Name of the page
   */
  trackPageView(pageName) {
    this.track(`page.${pageName}`, {
      page_name: pageName,
      title: document.title
    });
  }

  /**
   * Track search
   * @param {string} query - Search query
   * @param {string} context - Where the search was performed
   * @param {number} resultsCount - Number of results
   */
  trackSearch(query, context, resultsCount = null) {
    this.track('search', {
      query,
      context,
      results_count: resultsCount
    });
  }

  /**
   * Track feature usage
   * @param {string} feature - Feature name
   * @param {string} action - Action performed
   * @param {object} details - Additional details
   */
  trackFeature(feature, action, details = {}) {
    this.track(`feature.${feature}.${action}`, details);
  }

  /**
   * Track click
   * @param {string} element - Element identifier
   * @param {object} details - Additional details
   */
  trackClick(element, details = {}) {
    this.track('click', {
      element,
      ...details
    });
  }

  /**
   * Track error
   * @param {string} errorType - Type of error
   * @param {string} message - Error message
   * @param {object} context - Additional context
   */
  trackError(errorType, message, context = {}) {
    this.track('error', {
      error_type: errorType,
      message,
      ...context
    });
  }

  /**
   * Track export action
   * @param {string} dataType - Type of data exported
   * @param {string} format - Export format
   * @param {number} count - Number of records
   */
  trackExport(dataType, format, count) {
    this.track(`export.${dataType}`, {
      format,
      record_count: count
    });
  }

  /**
   * Track AI feature usage
   * @param {string} feature - AI feature name
   * @param {object} details - Additional details
   */
  trackAI(feature, details = {}) {
    this.track(`ai.${feature}`, details);
  }

  /**
   * Start batch processing
   */
  startBatchProcessing() {
    setInterval(() => {
      if (this.queue.length > 0) {
        this.flush();
      }
    }, this.flushInterval);

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flush(true);
    });
  }

  /**
   * Flush events to server
   * @param {boolean} sync - Use synchronous request (for beforeunload)
   */
  async flush(sync = false) {
    if (this.queue.length === 0 || this.isProcessing) return;

    this.isProcessing = true;
    const events = this.queue.splice(0, this.batchSize);

    try {
      const token = this.getToken();
      
      // Send events one by one (could be optimized to batch endpoint)
      for (const event of events) {
        if (sync) {
          // Use sendBeacon for sync requests
          const blob = new Blob([JSON.stringify(event)], { type: 'application/json' });
          navigator.sendBeacon(`${API_URL}/api/analytics/events/track`, blob);
        } else {
          await fetch(`${API_URL}/api/analytics/events/track`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify(event)
          });
        }
      }
    } catch (error) {
      // Re-queue failed events
      this.queue.unshift(...events);
      console.error('Failed to send events:', error);
    } finally {
      this.isProcessing = false;
    }
  }
}

// Singleton instance
const eventTracker = new EventTracker();

// Export both the instance and convenience functions
export default eventTracker;

export const trackPageView = (pageName) => eventTracker.trackPageView(pageName);
export const trackSearch = (query, context, resultsCount) => eventTracker.trackSearch(query, context, resultsCount);
export const trackFeature = (feature, action, details) => eventTracker.trackFeature(feature, action, details);
export const trackClick = (element, details) => eventTracker.trackClick(element, details);
export const trackError = (errorType, message, context) => eventTracker.trackError(errorType, message, context);
export const trackExport = (dataType, format, count) => eventTracker.trackExport(dataType, format, count);
export const trackAI = (feature, details) => eventTracker.trackAI(feature, details);
